import { QueryClient, QueryFunction } from "@tanstack/react-query";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    console.error(`API Error: ${res.status} - ${res.statusText}`, {
      url: res.url,
      status: res.status,
      statusText: res.statusText,
      text: text
    });
    
    // Try to extract the clean error message from JSON response
    let errorData;
    try {
      errorData = JSON.parse(text);
    } catch (parseError) {
      // If JSON parsing fails, use user-friendly generic messages
      console.log('JSON parsing failed:', parseError, 'Original text:', text);
    }
    
    // If we successfully parsed JSON, check for error messages
    if (errorData) {
      // Some endpoints (e.g. seller address/bank setup) validate every field
      // at once and return the full list, instead of failing on just the
      // first bad field — surface all of them together rather than only
      // the first, so the caller doesn't have to fix-one/resubmit/repeat.
      if (Array.isArray(errorData.errors) && errorData.errors.length > 0) {
        const messages = errorData.errors.map((e: any) => (typeof e === 'string' ? e : e.message)).filter(Boolean);
        const err = new Error(messages.join(' • '));
        (err as any).errors = errorData.errors;
        throw err;
      }
      if (errorData.error) {
        // Handle different error formats
        if (typeof errorData.error === 'string') {
          const err = new Error(errorData.error);
          if (errorData.reason) (err as any).reason = errorData.reason;
          // 2FA verify (and similar rate-limited endpoints) attach these so
          // callers can show a lockout countdown instead of just the message.
          if (errorData.locked_until) (err as any).lockedUntil = errorData.locked_until;
          if (errorData.attempts_remaining !== undefined) (err as any).attemptsRemaining = errorData.attempts_remaining;
          // subscription_required (rooms/recordings 403s) attaches this so
          // callers can link straight to the seller's profile to subscribe.
          if (errorData.sellerId) (err as any).sellerId = errorData.sellerId;
          throw err;
        } else if (typeof errorData.error === 'object') {
          // If error is an object, try to extract message
          const errorObj = errorData.error;
          if (errorObj.message) {
            throw new Error(errorObj.message);
          } else if (errorObj.error) {
            throw new Error(errorObj.error);
          } else {
            // Fallback: stringify the object for debugging
            throw new Error(`Validation failed: ${JSON.stringify(errorObj)}`);
          }
        }
      } else if (errorData.message) {
        // Fallback to message field if error field doesn't exist
        throw new Error(errorData.message);
      }
    }
    
    // Fallback: show user-friendly message based on status code instead of technical details
    if (res.status === 400) {
      throw new Error("Please check your information and try again.");
    } else if (res.status === 401) {
      // Only a sign-in attempt can be a bad password. Everywhere else a 401 means
      // the 30-day token expired, and telling the user their password is wrong
      // sends them to the reset flow for a problem a re-login fixes.
      const isAuthAttempt = /\/(login|signin|sign-in|register|signup|sign-up|auth)\b/i.test(res.url);
      throw new Error(
        isAuthAttempt
          ? "Invalid email or password."
          : "Your session has expired. Please sign in again."
      );
    } else if (res.status === 403) {
      throw new Error("Access denied. Please contact support.");
    } else if (res.status === 404) {
      throw new Error("Service not available. Please try again later.");
    } else if (res.status >= 500) {
      throw new Error("Something went wrong. Please try again later.");
    } else {
      throw new Error("Unable to complete request. Please try again.");
    }
  }
}

export async function apiRequest(
  method: string,
  url: string,
  data?: unknown | undefined,
): Promise<Response> {
  // Use local API endpoints (which proxy to Tokshop API)
  const apiUrl = url;
  
  // Get access token and user data from localStorage if available
  const adminToken = localStorage.getItem('adminAccessToken');
  const userToken = localStorage.getItem('accessToken');
  const userData = localStorage.getItem('user');
  
  const headers: Record<string, string> = data ? { "Content-Type": "application/json" } : {};
  
  // Send admin token if available (for admin routes)
  if (adminToken) {
    headers['x-admin-token'] = adminToken;
  }
  
  // Send regular user token if available (for all routes)
  if (userToken) {
    headers['x-access-token'] = userToken;
    // Also send as Authorization Bearer token for auction/bidding endpoints
    headers['Authorization'] = `Bearer ${userToken}`;
  }
  
  // Send user data for session restoration (base64 encoded to handle UTF-8 characters)
  if (userData) {
    headers['x-user-data'] = btoa(unescape(encodeURIComponent(userData)));
  }
  
  const res = await fetch(apiUrl, {
    method,
    headers,
    body: data ? JSON.stringify(data) : undefined,
    credentials: "include",
  }).catch(error => {
    console.error('Fetch error for:', apiUrl, error);
    throw error;
  });
  

  await throwIfResNotOk(res);
  return res;
}

type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
  async ({ queryKey }) => {
    const [endpoint, ...params] = queryKey as [string, ...any[]];
    
    // Build query parameters
    const queryParams = new URLSearchParams();
    let apiUrl = endpoint;
    
    // Handle different parameter patterns
    if (params.length > 0) {
      // For orders: ['/api/orders', userId, statusFilter, selectedCustomerId, currentPage, itemsPerPage]
      if (endpoint === '/api/orders' && params.length >= 5) {
        const [userId, statusFilter, selectedCustomerId, currentPage, itemsPerPage] = params;
        if (userId) queryParams.set('userId', String(userId));
        if (statusFilter && statusFilter !== 'all') queryParams.set('status', String(statusFilter));
        if (selectedCustomerId && selectedCustomerId !== 'all') queryParams.set('customerId', String(selectedCustomerId));
        if (currentPage) queryParams.set('page', String(currentPage));
        if (itemsPerPage) queryParams.set('limit', String(itemsPerPage));
      }
      // For admin shows: [page, limit, searchTitle, searchType]
      else if (endpoint === '/api/admin/shows' && params.length >= 2) {
        const [page, limit, searchTitle, searchType] = params;
        if (page) queryParams.set('page', String(page));
        if (limit) queryParams.set('limit', String(limit));
        if (searchTitle) queryParams.set('title', String(searchTitle));
        if (searchType) queryParams.set('type', String(searchType));
      }
      // For individual product/auction fetches: ['/api/products', productId]
      else if (endpoint === '/api/products' && params.length === 1 && typeof params[0] === 'string') {
        apiUrl = `${endpoint}/${params[0]}`;
      }
      // For individual auction fetches: ['/api/auction', auctionId]
      else if (endpoint === '/api/auction' && params.length === 1 && typeof params[0] === 'string') {
        apiUrl = `${endpoint}/${params[0]}`;
      }
      // For individual published article fetches: ['/api/articles/published', slug]
      else if (endpoint === '/api/articles/published' && params.length === 1 && typeof params[0] === 'string') {
        apiUrl = `${endpoint}/${params[0]}`;
      }
      // For other endpoints that use userId pattern
      else if (params[0] && typeof params[0] === 'string') {
        queryParams.set('userId', params[0]);
      }
    }
    
    // Build final URL with query parameters
    apiUrl = `${apiUrl}${queryParams.toString() ? '?' + queryParams.toString() : ''}`;
    
    // Get access token and user data from localStorage if available
    const adminToken = localStorage.getItem('adminAccessToken');
    const userToken = localStorage.getItem('accessToken');
    const userData = localStorage.getItem('user');
    
    const headers: Record<string, string> = {
      "Content-Type": "application/json",
    };
    
    // Send admin token if available (for admin routes)
    if (adminToken) {
      headers['x-admin-token'] = adminToken;
    }
    
    // Send regular user token if available (for all routes)
    if (userToken) {
      headers['x-access-token'] = userToken;
      // Also send as Authorization Bearer token for external API authentication
      headers['Authorization'] = `Bearer ${userToken}`;
    }
    
    // Send user data for session restoration (base64 encoded to handle UTF-8 characters)
    if (userData) {
      headers['x-user-data'] = btoa(unescape(encodeURIComponent(userData)));
    }
    
    const res = await fetch(apiUrl, {
      credentials: "include",
      headers,
    });

    if (unauthorizedBehavior === "returnNull" && res.status === 401) {
      return null;
    }

    await throwIfResNotOk(res);
    return await res.json();
  };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      // staleTime was Infinity with retry disabled, so on a live-commerce app
      // prices, stock, order status and wallet balances never refreshed after
      // first load, and one transient network blip failed a query permanently.
      refetchOnWindowFocus: true,
      staleTime: 30_000,
      retry: 1,
    },
    mutations: {
      // Mutations stay at retry: false — replaying a charge or a bid is worse
      // than surfacing the failure.
      retry: false,
    },
  },
});
